import PubNub from "pubnub";
import { API_ROUTES, fetchApi } from "@/config/api";
import { isSuccess } from "@/types/api";

interface PubNubTokenResponse {
  token: string;
  channel: string;
}

let pubnubInstance: PubNub | null = null;
let currentChannel: string | null = null;
let initializationPromise: Promise<PubNub> | null = null;

export const getPubNubChannel = (): string | null => currentChannel;
export const getPubNubInstance = (): PubNub | null => pubnubInstance;

const createPubNubInstance = async (): Promise<PubNub> => {
  const subscribeKey = process.env.NEXT_PUBLIC_PUBNUB_SUBSCRIBE_KEY;
  if (!subscribeKey) {
    throw new Error("PubNub subscribe key is not configured");
  }

  console.log("PubNub: fetching token");
  const response = await fetchApi<PubNubTokenResponse>(API_ROUTES.auth.pubnubToken);
  if (!isSuccess(response)) {
    throw new Error(response.error.message || "Failed to fetch PubNub token");
  }

  const { token, channel } = response.data;
  if (!token || !channel) {
    throw new Error("Invalid PubNub token response");
  }

  console.log("PubNub: token received", {
    hasToken: !!token,
    channel,
  });

  currentChannel = channel;
  const userId = channel.split(".")[1];

  console.log("PubNub: creating client", {
    hasSubscribeKey: !!subscribeKey,
    userId,
  });

  const pubnub = new PubNub({
    subscribeKey,
    authKey: token,
    ...(userId ? { userId } : {}),
  });

  pubnubInstance = pubnub;
  return pubnub;
};

export const initializePubNub = async (): Promise<PubNub> => {
  if (pubnubInstance) {
    return pubnubInstance;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  console.log("PubNub: initializing new instance");
  initializationPromise = createPubNubInstance().catch((error) => {
    initializationPromise = null;
    throw error;
  });

  return initializationPromise;
};

export const getPubNub = (): Promise<PubNub> => initializePubNub();

export const cleanupPubNub = () => {
  console.log("PubNub: cleanup start");
  if (pubnubInstance) {
    try {
      pubnubInstance.unsubscribeAll();
      if (typeof pubnubInstance.stop === "function") {
        pubnubInstance.stop();
      }
      if (typeof pubnubInstance.destroy === "function") {
        pubnubInstance.destroy();
      }
    } catch (error) {
      console.warn("PubNub cleanup error:", error);
    }
  }

  pubnubInstance = null;
  currentChannel = null;
  initializationPromise = null;
  console.log("PubNub: cleanup complete");
};
